import { platformTag } from "@m8t-stack/platform-release";
import type { InstalledStamp, PersonaStampEntry } from "@m8t-stack/platform-release";

const BARE_SEMVER = /^\d+\.\d+\.\d+$/;

export interface RunningBuildInput {
  /** The application's own version. Bare X.Y.Z — a date-and-commit tag cannot be represented. */
  version: string;
  gatewayTag: string;
  gatewayDigest: string;
  previousPlatformVersion: string | null;
  cliVersion: string;
  nowIso: string;
  /**
   * Carried forward from the existing record, untouched. This writer does not
   * deploy personas/infra/seed-delivery — it neither reads nor asserts on
   * them — so it must not overwrite what another writer already recorded
   * there. Pass the existing record's values (or the empty defaults below
   * when there IS no existing record — nothing to carry forward yet).
   */
  personas: Record<string, PersonaStampEntry>;
  infraTreeSha: string;
  brainSeeds?: { seedRev: string };
}

/**
 * Build the record for an install whose deployment is driven continuously rather than
 * by a published release.
 *
 * The honesty rule this encodes: report what this writer actually deploys, and nothing
 * else. It deploys the gateway, so the gateway is `managed` with its real tag and digest.
 * It does not deploy the agent components, so they are `external` with empty tags —
 * which downstream reporting drops entirely, leaving exactly the true claim. Filling
 * those in from a declared pin would assert versions this writer cannot verify.
 *
 * That rule cuts the other way for personas/infra/seed-delivery: this writer doesn't
 * own them, but the write below replaces the WHOLE record, so silently defaulting them
 * to empty would erase whatever another writer (converge, seed-stamp) already recorded
 * — a different statement ("this has no personas/infra/seed") from the true one ("this
 * writer doesn't know"). Carrying them forward untouched is what keeps that field a
 * report from whoever last legitimately wrote it, not from this one.
 */
export function buildRunningBuildStamp(input: RunningBuildInput): InstalledStamp {
  if (!BARE_SEMVER.test(input.version)) {
    throw new Error(
      `platform version must be bare X.Y.Z, got "${input.version}" — a build tag cannot be represented as a version.`,
    );
  }
  return {
    schemaVersion: 1,
    platformVersion: platformTag(input.version),
    previousPlatformVersion: input.previousPlatformVersion,
    updatedAt: input.nowIso,
    lastResult: "success",
    cli: input.cliVersion,
    origin: "cd",
    components: {
      gateway: { tag: input.gatewayTag, digest: input.gatewayDigest, state: "managed" },
      codingAgent: { tag: "", digest: "", state: "external" },
      azureExecutor: { tag: "", digest: "", state: "external" },
      personas: input.personas,
      infra: { treeSha: input.infraTreeSha },
      ...(input.brainSeeds ? { brainSeeds: input.brainSeeds } : {}),
    },
  };
}
